import { cloneElement, isValidElement, useId } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface FilterFieldProps {
    /**
     * Nhãn của bộ lọc. BẮT BUỘC và luôn hiển thị — placeholder không được đóng
     * vai nhãn vì nó biến mất ngay khi người dùng nhập, còn select thì sau khi
     * chọn chỉ còn thấy giá trị chứ không còn biết ô đó lọc theo gì (R7).
     * Cố ý không có prop nào để ẩn nhãn.
     */
    label: string
    /**
     * Control của bộ lọc. Hai cách dùng:
     *
     * 1. Truyền thẳng element — id tự sinh được gán vào chính element đó. Chỉ
     *    đúng khi element ấy là control thật, vd `<Input />`.
     * 2. Truyền hàm `(id) => ReactNode` — dùng khi control thật nằm sâu bên
     *    trong (input trong div bọc icon), hoặc khi id phải đặt ở chỗ khác
     *    (Radix `Select` root không render DOM, id phải nằm ở `SelectTrigger`).
     *
     * Id do FilterField tự sinh bằng `useId`, nên cùng một bộ lọc render ở
     * nhiều bề mặt (lưới desktop + sheet mobile) vẫn có id khác nhau. Đừng
     * hardcode id ở call site: trùng id trong DOM làm nhãn trỏ nhầm phần tử.
     */
    children: ReactNode | ((id: string) => ReactNode)
    className?: string
}

/** Bọc một control lọc: nhãn phía trên, control phía dưới. */
export function FilterField({ label, children, className }: FilterFieldProps) {
    const id = useId()
    const control =
        typeof children === 'function'
            ? children(id)
            : isValidElement(children)
              ? cloneElement(children as ReactElement<{ id?: string }>, { id })
              : children

    return (
        <div className={cn('grid min-w-0 gap-1.5', className)}>
            <Label htmlFor={id} className="text-muted-foreground text-xs">
                {label}
            </Label>
            {control}
        </div>
    )
}
